import React, { useState, useEffect, useRef } from 'react';
import { subscribeToCollection, saveDocument, deleteDocument } from '../firebase';
import DiscussionThread from './DiscussionThread';
import RoleSignupList from './RoleSignupList';
import { getUserName, isSameUser } from '../utils/userUtils';
import VoteButton from './VoteButton';
import { ensureAbsoluteUrl } from '../utils/urlUtils';

export default function LocalActivities({ users = [] }) {
  const [activities, setActivities] = useState([]);
  const [scheduleItems, setScheduleItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const initialOrderRef = useRef(null);

  // Add form states
  const [title, setTitle] = useState("");
  const [emoji, setEmoji] = useState("🏔️");
  const [desc, setDesc] = useState("");
  const [links, setLinks] = useState([{ label: "", url: "" }]);
  const [suggestedByInput, setSuggestedByInput] = useState("");

  // Edit form states
  const [editingActivityId, setEditingActivityId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editEmoji, setEditEmoji] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editLinks, setEditLinks] = useState([{ label: "", url: "" }]);
  const [editSuggestedBy, setEditSuggestedBy] = useState("");

  // Confirmed participant signup states
  const [signingUpId, setSigningUpId] = useState(null);
  const [userNameInput, setUserNameInput] = useState("");

  // Comment input state
  const [commentInputs, setCommentInputs] = useState({});

  // Expandable/collapsible state
  const [expandedActivities, setExpandedActivities] = useState({});
  const [expandedDiscussionIds, setExpandedDiscussionIds] = useState({});

  const handleLinkChange = (index, field, val) => {
    const newLinks = [...links];
    newLinks[index] = { ...newLinks[index], [field]: val };
    setLinks(newLinks);
  };

  const handleAddLinkRow = () => {
    setLinks([...links, { label: "", url: "" }]);
  };

  const handleRemoveLinkRow = (index) => {
    if (links.length > 1) {
      setLinks(links.filter((_, idx) => idx !== index));
    }
  };

  const handleEditLinkChange = (index, field, val) => {
    const newLinks = [...editLinks];
    newLinks[index] = { ...newLinks[index], [field]: val };
    setEditLinks(newLinks);
  };

  const handleAddEditLinkRow = () => {
    setEditLinks([...editLinks, { label: "", url: "" }]);
  };

  const handleRemoveEditLinkRow = (index) => {
    if (editLinks.length > 1) {
      setEditLinks(editLinks.filter((_, idx) => idx !== index));
    }
  };

  const toggleActivityExpanded = (id) => {
    setExpandedActivities(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  useEffect(() => {
    setIsLoading(true);
    // Subscribe to Activities
    const unsubscribe = subscribeToCollection("activities", (data) => {
      const sortedByVotes = [...data].sort((a, b) => (b.votes || 0) - (a.votes || 0));

      if (!initialOrderRef.current) {
        // First load: store the initial sorted order of activity IDs
        initialOrderRef.current = sortedByVotes.map(act => act.id);
        setActivities(sortedByVotes);
      } else {
        // Subsequent live updates (e.g. Liking/Voting): keep the exact initial order
        const orderMap = new Map(initialOrderRef.current.map((id, index) => [id, index]));
        const ordered = [...data].sort((a, b) => {
          const indexA = orderMap.has(a.id) ? orderMap.get(a.id) : Infinity;
          const indexB = orderMap.has(b.id) ? orderMap.get(b.id) : Infinity;
          if (indexA !== indexB) return indexA - indexB;
          return (b.votes || 0) - (a.votes || 0); // Fallback for new additions
        });
        setActivities(ordered);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Subscribe to schedule items so we can show scheduled dates on each activity card
  useEffect(() => {
    const unsubscribeSchedule = subscribeToCollection("schedule", (data) => {
      setScheduleItems(data);
    });
    return () => unsubscribeSchedule();
  }, []);

  // Deep-link handler: scroll to and highlight a specific activity via sessionStorage
  useEffect(() => {
    if (activities.length === 0) return;
    const targetId = sessionStorage.getItem('prine_focus_activity');
    if (!targetId) return;
    sessionStorage.removeItem('prine_focus_activity');
    // Expand the activity so it's fully visible before scrolling
    setExpandedActivities(prev => ({ ...prev, [targetId]: true }));
    // Delay scroll slightly so the expanded DOM has time to render
    setTimeout(() => {
      const el = document.getElementById(`activity-${targetId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.transition = 'box-shadow 0.3s ease, outline 0.3s ease';
        el.style.outline = '2px solid var(--color-secondary)';
        el.style.boxShadow = '0 0 0 4px rgba(245, 158, 11, 0.2)';
        setTimeout(() => {
          el.style.outline = '';
          el.style.boxShadow = '';
        }, 2200);
      }
    }, 120);
  }, [activities]);


  const handleVote = async (activity) => {
    const userName = localStorage.getItem("prine_user_name") || "";
    if (!userName.trim()) {
      alert("Please enter your name or log in to vote!");
      return;
    }

    const currentVotedBy = Array.isArray(activity.votedBy) ? activity.votedBy : [];
    let newVotedBy;
    const alreadyVoted = currentVotedBy.some(v => isSameUser(users, v, userName));
    if (alreadyVoted) {
      newVotedBy = currentVotedBy.filter(voter => !isSameUser(users, voter, userName));
    } else {
      newVotedBy = [...currentVotedBy, userName];
    }

    const updatedActivity = {
      ...activity,
      votedBy: newVotedBy,
      votes: newVotedBy.length
    };

    await saveDocument("activities", activity.id, updatedActivity);
  };

  const handleStartSignup = (activityId) => {
    setSigningUpId(activityId);
    const savedName = localStorage.getItem("prine_user_name") || (users[0]?.id || "");
    setUserNameInput(savedName);
  };

  const handleConfirmSignup = async (activity) => {
    if (!userNameInput.trim()) return alert("Please select your name.");

    const currentList = activity.whoSignedUp
      ? activity.whoSignedUp.split(',').map(n => n.trim()).filter(Boolean)
      : [];

    const newName = userNameInput.trim();
    const alreadySignedUp = currentList.some(item => isSameUser(users, item, newName));
    if (!alreadySignedUp) {
      currentList.push(newName);
    }

    const updated = {
      ...activity,
      whoSignedUp: currentList.join(", ")
    };

    await saveDocument("activities", activity.id, updated);
    setSigningUpId(null);
  };

  const handleLeaveActivity = async (activity, nameToLeave) => {
    if (!window.confirm(`Are you sure you want to remove ${getUserName(users, nameToLeave)} from this activity?`)) return;
    if (!activity.whoSignedUp) return;
    const currentList = activity.whoSignedUp.split(',').map(n => n.trim());
    const filtered = currentList.filter(n => !isSameUser(users, n, nameToLeave));

    const updated = {
      ...activity,
      whoSignedUp: filtered.join(", ")
    };
    await saveDocument("activities", activity.id, updated);
  };

  const handleStartEdit = (act) => {
    setEditingActivityId(act.id);
    setEditTitle(act.title);
    setEditEmoji(act.emoji || "🏔️");
    setEditDesc(act.description);
    setEditSuggestedBy(act.suggestedBy || "");
    if (act.links && act.links.length > 0) {
      setEditLinks(act.links.map(l => ({ label: l.label || "", url: l.url || "" })));
    } else {
      setEditLinks([{ label: "", url: "" }]);
    }
  };

  const handleSaveEdit = async (act) => {
    const updatedLinks = editLinks
      .map(l => ({ label: l.label.trim() || "Link", url: ensureAbsoluteUrl(l.url.trim()) }))
      .filter(l => l.url !== "");
    const updated = {
      ...act,
      title: editTitle.trim(),
      emoji: editEmoji.trim(),
      description: editDesc.trim(),
      links: updatedLinks,
      suggestedBy: editSuggestedBy
    };
    await saveDocument("activities", act.id, updated);
    setEditingActivityId(null);
  };

  const handleAddComment = async (activity) => {
    const text = commentInputs[activity.id] || "";
    if (!text.trim()) return;

    const user = localStorage.getItem("prine_user_name") || "Guest";
    const newComment = {
      id: `comm_${Date.now()}`,
      user,
      text: text.trim(),
      time: new Date().toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    };

    const currentComments = activity.comments || [];
    const updated = {
      ...activity,
      comments: [...currentComments, newComment]
    };

    await saveDocument("activities", activity.id, updated);
    setCommentInputs(prev => ({ ...prev, [activity.id]: "" }));
  };

  const handleDeleteComment = async (activity, commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) return;
    const currentComments = activity.comments || [];
    const updatedComments = currentComments.filter(c => c.id !== commentId);
    const updated = {
      ...activity,
      comments: updatedComments
    };
    await saveDocument("activities", activity.id, updated);
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm("Are you sure you want to delete this suggested activity?")) return;
    await deleteDocument("activities", activityId);
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    if (!title || !desc) return;

    const nominatorName = suggestedByInput || localStorage.getItem("prine_user_name") || "Guest";
    const updatedLinks = links
      .map(l => ({ label: l.label.trim() || "Link", url: ensureAbsoluteUrl(l.url.trim()) }))
      .filter(l => l.url !== "");

    const newActivity = {
      id: `act_${Date.now()}`,
      title: title.trim(),
      emoji,
      description: desc.trim(),
      votes: 0,
      votedBy: [],
      whoSignedUp: nominatorName,
      suggestedBy: nominatorName,
      links: updatedLinks,
      comments: []
    };

    await saveDocument("activities", newActivity.id, newActivity);
    setShowAddForm(false);
    // Reset Form
    setTitle("");
    setDesc("");
    setLinks([{ label: "", url: "" }]);
  };

  const handleToggleAddForm = () => {
    const isOpening = !showAddForm;
    setShowAddForm(isOpening);
    if (isOpening) {
      setSuggestedByInput(localStorage.getItem("prine_user_name") || (users[0]?.id || ""));
    }
  };

  return (
    <div className="tab-panel">
      {/* EXCURSION STYLE OVERRIDES FOR FLUSH HEADER AND LEFT ALIGNMENT */}
      <style dangerouslySetInnerHTML={{
        __html: `
        .excursion-card-overhaul {
          grid-template-columns: 1fr !important;
          align-items: stretch !important;
          width: 100% !important;
          box-sizing: border-box !important;
          gap: 20px !important;
        }
        .excursion-card-overhaul .activity-description {
          text-align: left !important;
        }
        .excursion-card-overhaul input,
        .excursion-card-overhaul textarea,
        .excursion-card-overhaul select {
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        .edit-link-row {
          display: grid !important;
          grid-template-columns: 2fr 3fr auto !important;
          gap: 8px !important;
          align-items: center !important;
          width: 100% !important;
          box-sizing: border-box !important;
        }
        @media (max-width: 500px) {
          .edit-link-row {
            grid-template-columns: 1fr !important;
            gap: 4px !important;
          }
          .edit-link-row button {
            align-self: flex-start !important;
            margin-top: 4px !important;
          }
        }
        @media (max-width: 768px) {
          .excursion-card-overhaul {
            grid-template-columns: 1fr !important;
            text-align: left !important;
            width: 100% !important;
            box-sizing: border-box !important;
          }
          .excursion-card-overhaul .activity-details-block {
            text-align: left !important;
          }
          .excursion-card-overhaul .activity-description {
            text-align: left !important;
          }
        }
      `}} />

      <div className="view-title-container">
        <div className="view-title">
          <h2>🏔️ Local Excursions and Activities</h2>
        </div>
        <button className="btn-primary" onClick={handleToggleAddForm}>
          {showAddForm ? "Cancel Suggestion" : "+ Suggest Activity"}
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleAddActivity} className="glass-panel" style={{ marginBottom: '32px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <h3 style={{ fontFamily: 'var(--font-title)', fontSize: '1.25rem', color: 'var(--color-text-main)' }}>Suggest an Activity</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Activity Title</label>
              <input type="text" placeholder="e.g. Rafting the Animas River" className="input-field" value={title} onChange={e => setTitle(e.target.value)} required />
            </div>
            <div className="form-group" style={{ maxWidth: '80px' }}>
              <label>Emoji</label>
              <input type="text" placeholder="☀️" className="input-field" value={emoji} onChange={e => setEmoji(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Suggested By</label>
              <select
                className="input-field"
                value={suggestedByInput}
                onChange={e => setSuggestedByInput(e.target.value)}
                required
                style={{ width: '100%' }}
              >
                <option value="" style={{ background: 'var(--color-bg-dropdown, #1e293b)', color: '#94a3b8' }}>-- Select Name --</option>
                {users.map(u => (
                  <option key={u.id} value={u.id} style={{ background: 'var(--color-bg-dropdown, #1e293b)', color: 'var(--color-text-main)' }}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontWeight: '600', fontSize: '0.9rem', color: 'var(--color-text-main)' }}>Reference Links</label>
            {links.map((link, idx) => (
              <div key={idx} className="edit-link-row" style={{ gap: '12px' }}>
                <input
                  type="text"
                  placeholder="Link Label (e.g. View Website)"
                  className="input-field"
                  value={link.label}
                  onChange={e => handleLinkChange(idx, 'label', e.target.value)}
                  style={{ fontFamily: 'var(--font-body)' }}
                />
                <input
                  type="text"
                  placeholder="Link URL (e.g. https://...)"
                  className="input-field"
                  value={link.url}
                  onChange={e => handleLinkChange(idx, 'url', e.target.value)}
                  style={{ fontFamily: 'var(--font-body)' }}
                />
                {links.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => handleRemoveLinkRow(idx)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--color-danger, #ef4444)',
                      cursor: 'pointer',
                      fontSize: '1.25rem',
                      fontWeight: 'bold',
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      fontFamily: 'var(--font-body)'
                    }}
                    title="Remove Link"
                  >
                    ×
                  </button>
                ) : (
                  <div style={{ width: '31px' }} />
                )}
              </div>
            ))}
            <button
              type="button"
              className="btn-secondary"
              onClick={handleAddLinkRow}
              style={{
                alignSelf: 'flex-start',
                padding: '4px 12px',
                fontSize: '0.8rem',
                borderRadius: '6px',
                marginTop: '4px',
                fontFamily: 'var(--font-body)'
              }}
            >
              + Add Another Link
            </button>
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea placeholder="Describe the activity, locations, rental details, or key info." className="input-field" value={desc} onChange={e => setDesc(e.target.value)} rows="3" required />
          </div>

          <button type="submit" className="btn-primary" style={{ alignSelf: 'flex-start' }}>Suggest Activity</button>
        </form>
      )}

      {isLoading ? (
        <div className="loading-container">
          <div className="loading-chip">
            <span className="spinner"></span>
            <span>Syncing Activities...</span>
          </div>
        </div>
      ) : activities.length === 0 ? (
        <div className="glass-panel" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)', width: '100%' }}>
          No activities suggested yet. Suggest one above!
        </div>
      ) : (
        <div className="activity-card-list">
          {activities.map(act => {
            const userName = localStorage.getItem("prine_user_name") || "";
            const currentVotedBy = Array.isArray(act.votedBy) ? act.votedBy : [];
            const hasVoted = userName ? currentVotedBy.includes(userName) : false;
            const signedUpList = act.whoSignedUp
              ? act.whoSignedUp.split(',').map(n => n.trim()).filter(Boolean)
              : [];
            const isEditing = editingActivityId === act.id;

            if (isEditing) {
              return (
                <div key={act.id} id={`activity-${act.id}`} className="activity-card excursion-card-overhaul" style={{ padding: '24px' }}>
                  <div className="activity-details-block" style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="text"
                        className="input-field"
                        value={editEmoji}
                        onChange={e => setEditEmoji(e.target.value)}
                        placeholder="☀️"
                        style={{ width: '50px', fontSize: '1.2rem', textAlign: 'center', fontFamily: 'var(--font-body)' }}
                      />
                      <input
                        type="text"
                        className="input-field"
                        value={editTitle}
                        onChange={e => setEditTitle(e.target.value)}
                        placeholder="Activity Title"
                        style={{ flex: 1, minWidth: '0', fontSize: '1.1rem', fontWeight: 'bold', fontFamily: 'var(--font-body)' }}
                      />
                    </div>
                    <textarea
                      className="input-field"
                      value={editDesc}
                      onChange={e => setEditDesc(e.target.value)}
                      placeholder="Activity description..."
                      rows="3"
                      style={{ fontSize: '0.9rem', width: '100%', fontFamily: 'var(--font-body)' }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                      <label style={{ fontWeight: '600', fontSize: '0.85rem', color: 'var(--color-text-main)' }}>Suggested By</label>
                      <select
                        className="input-field"
                        value={editSuggestedBy}
                        onChange={e => setEditSuggestedBy(e.target.value)}
                        style={{ fontSize: '0.9rem', fontFamily: 'var(--font-body)', width: '100%' }}
                        required
                      >
                        <option value="" style={{ background: 'var(--color-bg-dropdown, #1e293b)', color: '#94a3b8' }}>-- Select Name --</option>
                        {users.map(u => (
                          <option key={u.id} value={u.id} style={{ background: 'var(--color-bg-dropdown, #1e293b)', color: 'var(--color-text-main)' }}>
                            {u.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                      <label style={{ fontWeight: '600', fontSize: '0.85rem', color: 'var(--color-text-main)' }}>Reference Links</label>
                      {editLinks.map((link, idx) => (
                        <div key={idx} className="edit-link-row">
                          <input
                            type="text"
                            className="input-field"
                            value={link.label}
                            onChange={e => handleEditLinkChange(idx, 'label', e.target.value)}
                            placeholder="Link Label"
                            style={{ fontSize: '0.8rem', fontFamily: 'var(--font-body)' }}
                          />
                          <input
                            type="text"
                            className="input-field"
                            value={link.url}
                            onChange={e => handleEditLinkChange(idx, 'url', e.target.value)}
                            placeholder="Link URL"
                            style={{ fontSize: '0.8rem', fontFamily: 'var(--font-body)' }}
                          />
                          {editLinks.length > 1 ? (
                            <button
                              type="button"
                              onClick={() => handleRemoveEditLinkRow(idx)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--color-danger, #ef4444)',
                                cursor: 'pointer',
                                fontSize: '1.2rem',
                                fontWeight: 'bold',
                                padding: '0 6px',
                                display: 'flex',
                                alignItems: 'center',
                                fontFamily: 'var(--font-body)'
                              }}
                              title="Remove Link"
                            >
                              ×
                            </button>
                          ) : (
                            <div style={{ width: '25px' }} />
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={handleAddEditLinkRow}
                        style={{
                          alignSelf: 'flex-start',
                          padding: '4px 10px',
                          fontSize: '0.75rem',
                          borderRadius: '6px',
                          marginTop: '2px',
                          fontFamily: 'var(--font-body)'
                        }}
                      >
                        + Add Another Link
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <button
                        className="btn-primary"
                        onClick={() => handleSaveEdit(act)}
                        style={{ fontSize: '0.8rem', padding: '6px 16px', fontFamily: 'var(--font-body)' }}
                      >
                        Save
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={() => setEditingActivityId(null)}
                        style={{ fontSize: '0.8rem', padding: '6px 16px', fontFamily: 'var(--font-body)' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              );
            }

            const isExpanded = !!expandedActivities[act.id];
            const isActivityDiscussionExpanded = expandedDiscussionIds[act.id] !== undefined
              ? expandedDiscussionIds[act.id]
              : (act.comments || []).length > 0;

            return (
              <div key={act.id} id={`activity-${act.id}`} className="activity-card excursion-card-overhaul" style={{ padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                {/* Expandable Header Row */}
                <div
                  onClick={() => toggleActivityExpanded(act.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    width: '100%',
                    userSelect: 'none',
                    flexWrap: 'wrap',
                    gap: '12px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1.25rem' }}>
                      <span style={{ fontSize: '1.6rem' }}>{act.emoji || "🏔️"}</span> {act.title}
                    </h3>
                    {act.suggestedBy && (
                      <span className="activity-tag" style={{ color: 'var(--color-secondary)', background: 'rgba(245, 158, 11, 0.08)' }}>
                        Suggested by: {getUserName(users, act.suggestedBy)}
                      </span>
                    )}
                    {/* Schedule badges — show day/time if this activity is on the schedule */}
                    {scheduleItems.filter(s => s.activityId === act.id).map(s => (
                      <span key={s.id} style={{
                        fontSize: '0.7rem',
                        fontWeight: '700',
                        color: 'var(--color-primary)',
                        background: 'rgba(2, 132, 199, 0.08)',
                        border: '1px solid rgba(2, 132, 199, 0.15)',
                        padding: '3px 8px',
                        borderRadius: '12px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        {s.day} · {s.time}
                      </span>
                    ))}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                    <VoteButton
                      votes={act.votes || 0}
                      hasVoted={hasVoted}
                      onVote={() => handleVote(act)}
                      votedBy={currentVotedBy}
                      users={users}
                      itemType="activity"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Expand the main card if not already
                        setExpandedActivities(prev => ({ ...prev, [act.id]: true }));
                        // Toggle the discussion thread expansion
                        setExpandedDiscussionIds(prev => ({
                          ...prev,
                          [act.id]: !isActivityDiscussionExpanded
                        }));
                      }}
                      style={{
                        background: isActivityDiscussionExpanded ? 'rgba(2, 132, 199, 0.12)' : 'rgba(255, 255, 255, 0.03)',
                        border: isActivityDiscussionExpanded ? '1px solid rgba(2, 132, 199, 0.3)' : '1px solid rgba(255, 255, 255, 0.08)',
                        borderRadius: '20px',
                        padding: '4px 10px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        color: isActivityDiscussionExpanded ? 'var(--color-primary)' : 'var(--color-text-muted)',
                        fontWeight: '600',
                        transition: 'all 0.2s ease',
                        fontFamily: 'var(--font-body)'
                      }}
                      title="Reply / Discussion"
                    >
                      💬 {(act.comments || []).length}
                    </button>
                  </div>
                  {act.suggestedBy && userName && isSameUser(users, act.suggestedBy, userName) && (
                    <div style={{ display: 'inline-flex', gap: '6px' }} onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => handleStartEdit(act)}
                        className="btn-inline-edit"
                        title="Edit Suggestion"
                      >
                        [Edit]
                      </button>
                      <button
                        onClick={() => handleDeleteActivity(act.id)}
                        className="btn-inline-delete"
                        title="Delete Suggestion"
                      >
                        [Delete]
                      </button>
                    </div>
                  )}
                  <span style={{
                    fontSize: '0.75rem',
                    color: isExpanded ? 'var(--color-secondary)' : 'var(--color-primary)',
                    background: isExpanded ? 'rgba(245, 158, 11, 0.08)' : 'rgba(2, 132, 199, 0.08)',
                    border: isExpanded ? '1px solid rgba(245, 158, 11, 0.15)' : '1px solid rgba(2, 132, 199, 0.15)',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontWeight: '600',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s ease',
                    fontFamily: 'var(--font-body)'
                  }}>
                    {isExpanded ? "Collapse Details ▴" : "View Details ▾"}
                  </span>
                </div>

                {/* Collapsible Body */}
                {isExpanded && (
                  <div style={{ marginTop: '16px', borderTop: '1px solid var(--color-glass-border)', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <p className="activity-description" style={{ textAlign: 'left', margin: '0' }}>{act.description}</p>

                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {currentVotedBy.length > 0 && (
                        <div style={{
                          fontSize: '0.75rem',
                          color: 'var(--color-text-muted)',
                          background: 'rgba(255, 255, 255, 0.02)',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          border: '1px solid rgba(255, 255, 255, 0.03)'
                        }}>
                          <span>❤️ Liked by:</span>
                          <span style={{ color: 'var(--color-text-main)', fontWeight: '500' }}>
                            {currentVotedBy.map(v => getUserName(users, v)).join(', ')}
                          </span>
                        </div>
                      )}

                      <a
                        href={`#schedule?activity=${act.id}`}
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--color-primary)',
                          background: 'rgba(2, 132, 199, 0.08)',
                          border: '1px solid rgba(2, 132, 199, 0.18)',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          textDecoration: 'none',
                          fontWeight: '600',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          transition: 'all 0.2s ease',
                          fontFamily: 'var(--font-body)'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.background = 'rgba(2, 132, 199, 0.15)';
                          e.currentTarget.style.borderColor = 'var(--color-primary)';
                          e.currentTarget.style.boxShadow = '0 0 8px rgba(2, 132, 199, 0.2)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = 'rgba(2, 132, 199, 0.08)';
                          e.currentTarget.style.borderColor = 'rgba(2, 132, 199, 0.18)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        Put on Schedule
                      </a>
                    </div>

                    {/* Clickable Links */}
                    {act.links && act.links.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {act.links.map((link, idx) => (
                          <a
                            key={idx}
                            href={ensureAbsoluteUrl(link.url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="activity-tag"
                            style={{
                              color: 'var(--color-secondary)',
                              background: 'rgba(245, 158, 11, 0.06)',
                              borderColor: 'rgba(245, 158, 11, 0.15)',
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontWeight: '600',
                              fontFamily: 'var(--font-body)'
                            }}
                          >
                            🔗 {link.label}
                          </a>
                        ))}
                      </div>
                    )}

                    {/* Who is Going Signups */}
                    <RoleSignupList
                      label="Confirmed Participants"
                      names={signedUpList}
                      isSigningUp={signingUpId === act.id}
                      userNameInput={userNameInput}
                      onUserNameChange={setUserNameInput}
                      onStartSignup={() => handleStartSignup(act.id)}
                      onConfirmSignup={() => handleConfirmSignup(act)}
                      onCancelSignup={() => setSigningUpId(null)}
                      onLeave={(name) => handleLeaveActivity(act, name)}
                      users={users}
                      buttonLabel="+ Join Activity"
                      borderColor="rgba(2, 132, 199, 0.18)"
                      accentColor="var(--color-primary)"
                    />

                    {/* Micro Comment Threads */}
                    <DiscussionThread
                      comments={act.comments}
                      commentInput={commentInputs[act.id] || ""}
                      onCommentInputChange={(val) => setCommentInputs(prev => ({ ...prev, [act.id]: val }))}
                      onAddComment={() => handleAddComment(act)}
                      onDeleteComment={(commentId) => handleDeleteComment(act, commentId)}
                      placeholder="Comment on this activity..."
                      users={users}
                      isExpanded={isActivityDiscussionExpanded}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
